<template>
  <div class="zhihu-answer-page">
    <!-- 主体内容区域 -->
    <main class="zhihu-main">
      <el-row :gutter="24" type="flex" justify="center">

        <!-- 左侧：回答列表主体 -->
        <el-col :xs="24" :sm="24" :md="16" :lg="14" class="main-column">

          <!-- 问题卡片 (来自 getQuestion 接口) -->
          <div class="question-card">
            <div class="tags-container">
              <el-tag v-for="(tag, index) in question.tags" :key="index" size="mini" effect="plain" class="question-tag">{{ tag }}</el-tag>
            </div>
            <h1 class="question-title">{{ question.title }}</h1>
          </div>

          <!-- 回答列表区域 (来自 getAnswer 接口的 list) -->
          <div v-for="answer in answerList" :key="answer.answerId" class="answer-card">
            <!-- 作者信息栏 -->
            <div class="author-info">
              <el-avatar :size="40" :src="answer.author.avatar" class="author-avatar" />
              <div class="author-meta">
                <a
                  style="text-decoration: none; color: inherit;"
                  target="_blank"
                  rel="noopener noreferrer"
                  :href="`https://www.zhihu.com/people/${answer.author.userId}`"
                >
                  <div class="author-name">{{ answer.author.name }}</div>
                </a>
                <div class="author-headline">{{ answer.author.headline || '暂无签名' }}</div>
              </div>
              <el-button type="primary" size="small" icon="el-icon-plus" class="follow-btn">关注他</el-button>
            </div>

            <!-- 回答正文 -->
            <div class="answer-content" v-html="formatRichContent(answer.content)" />

            <!-- 发布/编辑时间 -->
            <a
              style="text-decoration: none; color: inherit;"
              target="_blank"
              rel="noopener noreferrer"
              :href="`https://www.zhihu.com/question/${answer.questionId}/answer/${answer.answerId}`"
            >
              <div class="publish-time">编辑于 {{ answer.updatedTime }}</div>
            </a>

            <!-- 底部互动工具栏 -->
            <div class="answer-actions">
              <el-button type="primary" size="small" class="action-btn vote-up" @click="handleVoteUp(answer)">
                <i class="el-icon-caret-top" /> 赞同 {{ answer.voteupCount }}
              </el-button>
              <el-button type="primary" size="small" class="action-btn vote-down" @click="handleVoteDown">
                <i class="el-icon-caret-down" />
              </el-button>

              <el-button type="text" icon="el-icon-s-comment" class="text-action" @click="toggleComments(answer)">
                {{ answer.commentCount }} 条评论
              </el-button>
              <el-button type="text" icon="el-icon-share" class="text-action">分享</el-button>
              <el-button type="text" icon="el-icon-star-on" class="text-action">收藏</el-button>
              <el-button type="text" icon="el-icon-s-opportunity" class="text-action">喜欢</el-button>
            </div>

            <!-- 评论区展开折叠部分（针对当前回答） -->
            <div v-if="answer.showComments" class="comments-section">
              <div class="comments-header">
                <h3>{{ answer.commentCount }} 条评论</h3>
                <span class="close-comments" @click="answer.showComments = false"><i class="el-icon-close" /> 收起</span>
              </div>

              <!-- 发表评论输入框 -->
              <div class="comment-input-box">
                <el-input
                  v-model="answer.newCommentText"
                  type="textarea"
                  :rows="2"
                  placeholder="写下你的评论..."
                  size="small"
                />
                <div class="comment-submit-row">
                  <el-button type="primary" size="small" @click="submitComment(answer)">发布</el-button>
                </div>
              </div>

              <!-- 一级评论列表 (绑定真实接口数据 commentList) -->
              <div v-loading="answer.commentLoading" class="comment-list">
                <div v-for="comment in answer.commentList" :key="comment.commentId" class="comment-item">
                  <el-avatar :size="32" :src="comment.author.avatar" class="comment-avatar" />
                  <div class="comment-detail">
                    <div class="comment-user">
                      <a
                        style="text-decoration: none; color: inherit;"
                        target="_blank"
                        rel="noopener noreferrer"
                        :href="`https://www.zhihu.com/people/${comment.author.userId}`"
                      >
                        {{ comment.author.name }}
                      </a>
                      <span v-if="comment.location" class="comment-location">· IP属地 {{ comment.location }}</span>
                    </div>
                    <!-- 一级评论内容支持HTML解析 -->
                    <div class="comment-text" v-html="formatRichContent(comment.content)" />

                    <div class="comment-footer">
                      <span class="time">{{ comment.commentCreated }}</span>
                      <span class="action-item"><i class="el-icon-caret-top" /> {{ comment.likeCount }}</span>
                      <span class="action-item"><i class="el-icon-caret-down" /></span>
                      <span class="reply-action" @click="toggleChildComments(comment)">
                        <i class="el-icon-chat-square" />
                        {{ comment.childCommentCount > 0 ? `${comment.childCommentCount}条回复` : '回复' }}
                      </span>
                    </div>

                    <!-- 二级子评论/回复展开区域 -->
                    <div v-if="comment.showChild" v-loading="comment.childLoading" class="child-comments-section">
                      <div v-for="child in comment.childList" :key="child.commentId" class="child-comment-item">
                        <el-avatar :size="24" :src="child.author.avatar" class="comment-avatar" />
                        <div class="comment-detail">
                          <div class="comment-user">
                            <a
                              style="text-decoration: none; color: inherit;"
                              target="_blank"
                              rel="noopener noreferrer"
                              :href="`https://www.zhihu.com/people/${child.author.userId}`"
                            >
                              {{ child.author.name }}
                            </a>
                            <span v-if="child.location" class="comment-location">· IP属地 {{ child.location }}</span>
                          </div>
                          <div class="comment-text" v-html="formatRichContent(child.content)" />
                          <div class="comment-footer">
                            <span class="time">{{ child.commentCreated }}</span>
                            <span class="action-item"><i class="el-icon-caret-top" /> {{ child.likeCount }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <!-- 空状态提示 -->
                <div v-if="!answer.commentLoading && (!answer.commentList || answer.commentList.length === 0)" class="empty-comments">
                  暂无评论，快来抢沙发吧~
                </div>
              </div>
            </div>

          </div>

          <!-- 分页组件 -->
          <div class="pagination-container" v-if="totalSize > 0">
            <el-pagination
              background
              layout="total, prev, pager, next, jumper"
              :current-page.sync="queryInfo.pn"
              :page-size="queryInfo.pageSize"
              :total="totalSize"
              @current-change="handlePageChange"
            />
          </div>

        </el-col>

        <!-- 右侧：边栏 (Sidebar) -->
        <el-col :xs="0" :sm="0" :md="7" :lg="6" class="sidebar-column">
          <!-- 作者名片卡片 -->
          <div v-if="answerList.length > 0" class="sidebar-card author-card">
            <div class="card-header">
              <el-avatar :size="50" :src="answerList[0].author.avatar" />
              <div class="card-author-info">
                <div class="name">{{ answerList[0].author.name }}</div>
                <div class="headline">{{ answerList[0].author.headline || '知乎用户' }}</div>
              </div>
            </div>
            <div class="card-stats">
              <div class="stat-item">
                <div class="label">回答</div>
                <div class="value">124</div>
              </div>
              <div class="stat-item">
                <div class="label">文章</div>
                <div class="value">12</div>
              </div>
              <div class="stat-item">
                <div class="label">关注者</div>
                <div class="value">8.5w</div>
              </div>
            </div>
            <div class="card-actions">
              <el-button type="primary" plain size="medium" style="width: 100%;">发消息</el-button>
            </div>
          </div>

          <!-- 相关问题推荐 -->
          <div class="sidebar-card related-card">
            <div class="card-title">相关问题</div>
            <ul class="related-list">
              <li v-for="(item, index) in relatedQuestions" :key="index">
                <a href="javascript:;">{{ item.title }}</a>
                <span class="answer-num">{{ item.answers }} 回答</span>
              </li>
            </ul>
          </div>
        </el-col>

      </el-row>
    </main>
  </div>
</template>

<script>
import { getAnswer, getComment, getChildComment, getQuestion } from '@/api/zhihu'

export default {
  name: 'ZhihuPage',
  data() {
    return {
      question: {
        title: '',
        tags: []
      },
      answerList: [],
      // 分页相关状态
      queryInfo: {
        pn: 1,
        pageSize: 10
      },
      totalSize: 0,
      relatedQuestions: [
        { title: '如何看待当前大学城安全管理与治安防范？', answers: '324' },
        { title: '面对突发恶性事件，普通人应该如何最大限度保护自己？', answers: '512' },
        { title: '社会心理压力增大时，如何进行自我心理调节？', answers: '1.2w' }
      ]
    }
  },
  created() {
    this.getData()
  },
  methods: {
    // 清洗知乎富文本：提取真实的图片地址
    formatRichContent(html) {
      if (!html) return ''

      const div = document.createElement('div')
      div.innerHTML = html

      // 1. 处理懒加载图片
      const lazyImages = div.querySelectorAll('img.lazy, img[data-actualsrc], img[data-src]')
      lazyImages.forEach(img => {
        const realSrc = img.getAttribute('data-actualsrc') || img.getAttribute('data-src')
        if (realSrc) {
          img.setAttribute('src', realSrc)
        }
        img.classList.remove('lazy')
      })

      // 2. 释放 noscript 标签中的真实图片
      const noscripts = div.querySelectorAll('noscript')
      noscripts.forEach(noscript => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = noscript.textContent || noscript.innerHTML
        const noscriptImg = tempDiv.querySelector('img')
        if (noscriptImg && noscript.parentNode) {
          noscript.parentNode.replaceChild(noscriptImg, noscript)
        }
      })

      return div.innerHTML
    },

    getData() {
      const questionIdFromRoute = String(this.$route.params.questionId || '')
      if (!questionIdFromRoute) {
        this.$message.error('缺少问题ID参数')
        return
      }

      // 1. 获取问题详情（仅在初始化或需要时获取一次即可）
      if (!this.question.title) {
        getQuestion({ questionId: questionIdFromRoute }).then(resp => {
          if (resp.code === 0 && resp.data) {
            this.question = resp.data
          }
        })
      }

      // 2. 获取回答列表（带分页参数 pn 和 pageSize）
      const params = {
        questionId: questionIdFromRoute,
        pn: this.queryInfo.pn,
        pageSize: this.queryInfo.pageSize
      }

      getAnswer(params).then(resp => {
        if (resp.code === 0 && resp.data) {
          // 映射回答列表，注入交互状态
          this.answerList = (resp.data.list || []).map(item => ({
            ...item,
            showComments: false,
            commentLoading: false,
            commentList: [],
            newCommentText: ''
          }))
          // 更新总记录数
          this.totalSize = resp.data.totalSize || 0
          if (resp.data.pageNumber) {
            this.queryInfo.pn = resp.data.pageNumber
          }
        }
      }).catch(err => {
        console.error('获取回答列表失败', err)
      })
    },

    // 翻页事件触发
    handlePageChange(newPage) {
      this.queryInfo.pn = newPage
      this.getData()
      // 切换页码后平滑滚动回顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // 点击切换评论区展开/收起，并自动请求一级评论
    toggleComments(answer) {
      answer.showComments = !answer.showComments

      if (answer.showComments && answer.commentList.length === 0) {
        this.fetchComments(answer)
      }
    },

    // 请求一级评论接口 (getComment) -> 保证 answerId 为字符串
    fetchComments(answer) {
      answer.commentLoading = true
      const params = { answerId: String(answer.answerId), pn: 1 }

      getComment(params).then(resp => {
        answer.commentLoading = false
        if (resp.code === 0 && resp.data && resp.data.list) {
          const comments = resp.data.list.map(comment => ({
            ...comment,
            showChild: false,
            childLoading: false,
            childList: []
          }))
          this.$set(answer, 'commentList', comments)
        }
      }).catch(() => {
        answer.commentLoading = false
      })
    },

    // 点击展开/收起二级子评论
    toggleChildComments(comment) {
      if (!comment.childCommentCount || comment.childCommentCount <= 0) {
        return
      }

      comment.showChild = !comment.showChild

      if (comment.showChild && comment.childList.length === 0) {
        this.fetchChildComments(comment)
      }
    },

    // 请求二级子评论接口 (getChildComment) -> 保证 commentId 为字符串
    fetchChildComments(comment) {
      comment.childLoading = true
      const params = { commentId: String(comment.commentId), pn: 1 }

      getChildComment(params).then(resp => {
        comment.childLoading = false
        if (resp.code === 0 && resp.data && resp.data.list) {
          this.$set(comment, 'childList', resp.data.list)
        }
      }).catch(() => {
        comment.childLoading = false
      })
    },

    handleVoteUp(answer) {
      answer.voteupCount++
      this.$message.success('已赞同')
    },
    handleVoteDown() {
      this.$message.info('已踩')
    },

    // 提交顶层评论（前端本地追加展示）
    submitComment(answer) {
      if (!answer.newCommentText || !answer.newCommentText.trim()) {
        this.$message.warning('评论内容不能为空')
        return
      }

      const newCommentItem = {
        commentId: Date.now(),
        parentType: 'answer',
        parentId: String(answer.answerId),
        content: answer.newCommentText,
        commentCreated: '刚刚',
        likeCount: 0,
        dislikeCount: 0,
        location: '本地',
        childCommentCount: 0,
        author: {
          userId: 'current_user',
          name: '当前登录用户',
          avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
          headline: '前端开发工程师'
        },
        showChild: false,
        childLoading: false,
        childList: []
      }

      answer.commentList.unshift(newCommentItem)
      answer.commentCount++
      answer.newCommentText = ''
      this.$message.success('评论发表成功')
    }
  }
}
</script>

<style scoped>
/* 基础样式复用 */
.zhihu-answer-page {
  background-color: #f6f6f6;
  min-height: 100vh;
  color: #121212;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-bottom: 40px;
}
.zhihu-main {
  max-width: 1030px;
  margin: 10px auto 0;
  padding: 0 10px;
}
.question-card, .answer-card, .sidebar-card {
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(18, 18, 18, 0.04);
  padding: 20px;
  margin-bottom: 10px;
}
.question-tag {
  margin-right: 10px;
  background-color: rgba(0, 102, 255, 0.1);
  color: #0066ff;
  border: none;
}
.question-title {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 10px;
  margin-bottom: 0;
}
.author-info {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}
.author-meta {
  margin-left: 12px;
  flex: 1;
}
.author-name {
  font-weight: 600;
  font-size: 15px;
}
.author-headline {
  font-size: 13px;
  color: #646464;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 450px;
}
.follow-btn {
  background-color: rgba(0, 102, 255, 0.1);
  color: #0066ff;
  border: none;
}
.answer-content {
  font-size: 15px;
  line-height: 1.7;
  color: #121212;
}
.answer-content >>> p { margin-bottom: 1em; }
.publish-time {
  font-size: 13px;
  color: #8590a6;
  margin: 15px 0;
}
.answer-actions {
  display: flex;
  align-items: center;
}
.vote-up {
  background-color: rgba(0, 102, 255, 0.1) !important;
  color: #0066ff !important;
  border: none !important;
}
.vote-down {
  background-color: rgba(0, 102, 255, 0.1) !important;
  color: #0066ff !important;
  border: none !important;
  padding: 8px 10px !important;
}
.text-action {
  color: #8590a6 !important;
  margin-left: 20px;
}

/* 分页组件样式 */
.pagination-container {
  background: #fff;
  padding: 20px;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(18, 18, 18, 0.04);
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

/* 评论区专属样式 */
.comments-section {
  margin-top: 15px;
  border-top: 1px solid #f0f0f0;
  padding-top: 15px;
}
.comments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.comments-header h3 { font-size: 15px; margin: 0; }
.close-comments { font-size: 13px; color: #8590a6; cursor: pointer; }
.comment-input-box { margin-bottom: 20px; }
.comment-submit-row { display: flex; justify-content: flex-end; margin-top: 8px; }

/* 评论列表项 */
.comment-item {
  display: flex;
  margin-bottom: 16px;
  font-size: 14px;
}
.comment-avatar { margin-right: 10px; flex-shrink: 0; }
.comment-detail {
  flex: 1;
  border-bottom: 1px solid #f9f9f9;
  padding-bottom: 12px;
}
.comment-user {
  font-weight: 600;
  color: #444;
  margin-bottom: 4px;
}
.comment-location {
  font-weight: normal;
  font-size: 12px;
  color: #8590a6;
  margin-left: 6px;
}
.comment-text {
  color: #121212;
  line-height: 1.5;
}
.comment-text >>> p { margin: 0; }
.comment-footer {
  margin-top: 8px;
  font-size: 12px;
  color: #8590a6;
  display: flex;
  align-items: center;
}
.comment-footer .time {
  margin-right: 15px;
}
.comment-footer .action-item {
  margin-right: 15px;
  cursor: pointer;
}
.reply-action {
  cursor: pointer;
  color: #646464;
}
.reply-action:hover { color: #0066ff; }
.empty-comments {
  text-align: center;
  color: #8590a6;
  padding: 20px 0;
  font-size: 13px;
}

/* 二级子评论区域 */
.child-comments-section {
  margin-top: 12px;
  background-color: #f9fafc;
  padding: 10px 12px;
  border-radius: 4px;
}
.child-comment-item {
  display: flex;
  margin-top: 10px;
  font-size: 13px;
}
.child-comment-item:first-child {
  margin-top: 0;
}

/* 侧边栏样式 */
.author-card .card-header { display: flex; align-items: center; margin-bottom: 15px; }
.card-author-info { margin-left: 12px; }
.card-author-info .name { font-weight: 600; font-size: 15px; }
.card-author-info .headline { font-size: 12px; color: #646464; margin-top: 2px; }
.card-stats { display: flex; justify-content: space-around; margin-bottom: 15px; text-align: center; }
.stat-item .label { font-size: 12px; color: #8590a6; }
.stat-item .value { font-size: 15px; font-weight: 600; color: #121212; margin-top: 2px; }
.related-card .card-title { font-size: 15px; font-weight: 600; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0; margin-bottom: 12px; }
.related-list { list-style: none; padding: 0; margin: 0; }
.related-list li { margin-bottom: 12px; font-size: 13px; display: flex; justify-content: space-between; }
.related-list a { color: #121212; text-decoration: none; flex: 1; margin-right: 10px; }
.related-list .answer-num { color: #8590a6; white-space: nowrap; }

/* 让富文本中的图片正常自适应展示 */
.answer-content >>> img,
.comment-text >>> img {
  display: block;
  max-width: 100%;
  height: auto !important;
  border-radius: 4px;
  margin: 10px 0;
}

/* 隐藏知乎富文本里不需要的 noscript 原始占位 */
.answer-content >>> noscript,
.comment-text >>> noscript {
  display: none;
}

/* 适配知乎的 figure 标签 */
.answer-content >>> figure,
.comment-text >>> figure {
  margin: 10px 0;
  max-width: 100%;
}
</style>
